import { useEffect, useState } from 'react'
import { APP_VERSION } from './version'
import { SPA_VERSION_HEADER } from './client-floor'

/**
 * The bridge to Niobe Desktop (`client/desktop`).
 *
 * `window.niobeDesktop` is injected by the desktop app's preload script and is absent in a normal
 * browser, so its presence is the reliable "am I running inside the desktop app?" signal — more
 * reliable than sniffing the user agent, which Electron lets anyone change.
 *
 * Imports nothing from the app, so it can be used from the lowest layers (the AI capability gate
 * consults it) without creating cycles.
 */

/**
 * The desktop app's electron-updater/SPA-fastlane state, as reported by the main process. `app` and
 * `spa` are each one of `'off' | 'idle' | 'checking' | 'downloading' | 'ready' | 'error'` — kept as
 * plain strings here (rather than a union) since this side only ever compares against `'ready'`.
 */
export type UpdateState = {
  app: string
  spa: string
  appVersion: string | null
  spaVersion: string | null
  error: string | null
}

declare global {
  interface Window {
    niobeDesktop?: {
      isDesktop: true
      version?: string
      /** Niobe Desktop's OWN version (distinct from `version` above, the Electron runtime's) —
       *  the footer's "Desktop vX.Y.Z". The SPA's own version needs no equivalent round trip: it
       *  is already baked into this very bundle as APP_VERSION (lib/version.ts). */
      appVersion?: string
      /** Tell the desktop tray who is signed in. See client/desktop/preload.mjs. */
      reportAccount?: (name: string) => void
      updates?: {
        get: () => Promise<UpdateState | null>
        onChange: (cb: (state: UpdateState | null) => void) => () => void
        restart: () => Promise<{ ok: true }>
        /** Check both lanes now, past the rate limit (lib/client-floor.ts asks when this tab is
         *  below the backend's version floor). Absent on shells older than that change. */
        check?: () => Promise<UpdateState | null>
      }
      /**
       * The opt-in long-lived Claude token (`claude setup-token`), when the desktop shell's
       * `claudeLongLivedToken` flag is on.
       *
       * **Its presence IS the capability gate** — the same shape `auth` uses. The preload only
       * spreads this object in when the flag resolved true, so the Settings panel renders on
       * `window.niobeDesktop?.claudeToken` and no second boolean has to be kept in sync. Absent in a
       * plain browser, with the flag off, and in dev-live (where the passerelle is a separate
       * process the shell cannot reach — see DEBT.md).
       *
       * No method ever returns the token: `status()` is metadata only.
       */
      claudeToken?: ClaudeTokenBridge
      /** Finished downloads, for the export dialog's file / folder links (AB#415). Absent on
       *  shells older than that change — see {@link downloadsBridge}. */
      downloads?: DownloadsBridge
    }
  }
}

/** A download the desktop shell saw finish. The id is opaque: the shell keeps the path. `folder` is
 *  the containing folder's NAME only, never its path. */
export type FinishedDownload = { id: string; filename: string; folder: string }

/** The desktop shell's finished-downloads bridge (client/desktop/preload.mjs, downloads.js). */
export type DownloadsBridge = {
  onDone: (cb: (download: FinishedDownload) => void) => () => void
  /** Open the file with its default app (`shell.openPath`). */
  open: (id: string) => Promise<{ ok: boolean; reason?: string }>
  /** Show it in Explorer/Finder, selected (`shell.showItemInFolder`). */
  reveal: (id: string) => Promise<{ ok: boolean; reason?: string }>
}

/**
 * The shell's downloads bridge, or null — in a plain browser, and in an older Desktop shell that
 * predates it (SPA/shell skew: the SPA can fastlane past the shell). Feature-detected, so null
 * simply means "this page cannot open what it saved".
 */
export function downloadsBridge(): DownloadsBridge | null {
  const d = window.niobeDesktop?.downloads
  return d && typeof d.onDone === 'function' && typeof d.open === 'function' && typeof d.reveal === 'function'
    ? d
    : null
}

/** What `claudeToken.status()` reports. Never the token, never a fragment of it. */
export type ClaudeTokenStatus = {
  /** Is a token stored on this machine at all? True even once it has been rejected — the panel
   *  still has to offer Remove / Generate a new one. */
  present: boolean
  /** Epoch ms. When the member saved it. */
  savedAt?: number
  /** Epoch ms, `savedAt` + 365 d. An ESTIMATE: the CLI never prints the real expiry (DEBT.md). */
  expiresAt?: number
  /** Niobe stopped injecting it after a run was refused. A sign-in is a valid fix again. */
  rejected?: boolean
  rejectedAt?: number
  /** The CLI's own sentence for the refusal, already redacted shell-side. */
  rejectedReason?: string
  /** `false` = the OS had no keyring, so the token lives only until Niobe is closed. */
  persisted?: boolean
  /** Only ever `'unauthorized'` — the shell refused a call that did not come from this page. */
  reason?: string
}

/** The desktop shell's long-lived-token bridge (client/desktop/preload.mjs). */
export type ClaudeTokenBridge = {
  status: () => Promise<ClaudeTokenStatus>
  /** Validated, then probed against Claude before anything is stored. */
  set: (token: string) => Promise<{ ok: boolean; reason?: string; kind?: string; persisted?: boolean }>
  remove: () => Promise<{ ok: boolean; reason?: string }>
  /** Opens a visible console running `claude setup-token`. `supported:false` on macOS/Linux, where
   *  `command` is what the member runs themselves. */
  openSetupTerminal: () => Promise<{ supported: boolean; opened?: boolean; command?: string; error?: string }>
}

/** The shell's token bridge, or null — absent in a browser, with the flag off, and in dev-live. */
export function claudeTokenBridge(): ClaudeTokenBridge | null {
  return window.niobeDesktop?.claudeToken ?? null
}

/**
 * Are we running inside Niobe Desktop? Synchronous, and cannot change for the life of the page,
 * so it is safe to read from anywhere including non-render code.
 */
export function isDesktopApp(): boolean {
  return Boolean(window.niobeDesktop?.isDesktop)
}

/**
 * The headers every authenticated call to the backend carries, saying WHICH client is
 * speaking and at what version: `X-Niobe-Client` (`desktop` | `web`),
 * `X-Niobe-Client-Version`, and `X-Niobe-Spa-Version` (this bundle's own version, in both — the
 * one the backend's SPA_MIN_VERSION floor is about, see lib/client-floor.ts).
 *
 * The Bearer cannot answer this — the same member holds the same token in the browser and inside
 * Niobe Desktop. Without these headers the backend's access log records who called and never how,
 * which is exactly the question Settings → User activity exists to answer ("is that person still
 * on an old installer?"). The backend sanitises both (closed set, charset + length cap) and
 * stores null for anything it refuses, so nothing here is trusted on the far side.
 *
 * Inside the desktop app the version is the DESKTOP build (`window.niobeDesktop.appVersion`), not
 * this bundle's — a desktop install can fastlane a newer SPA into an older shell, and the shell
 * is what an admin would have to ask someone to reinstall. It is omitted rather than guessed when
 * the preload did not supply one.
 */
export function clientHeaders(): Record<string, string> {
  const desktop = window.niobeDesktop
  if (desktop?.isDesktop) {
    return {
      'X-Niobe-Client': 'desktop',
      ...(desktop.appVersion ? { 'X-Niobe-Client-Version': desktop.appVersion } : {}),
      [SPA_VERSION_HEADER]: APP_VERSION,
    }
  }
  return { 'X-Niobe-Client': 'web', 'X-Niobe-Client-Version': APP_VERSION, [SPA_VERSION_HEADER]: APP_VERSION }
}

/**
 * Report the signed-in member to the desktop tray.
 *
 * The main process cannot discover this itself: the MSAL session lives in the renderer's
 * sessionStorage, and reading MSAL's cache format from outside would break on any of its
 * upgrades. So the page states it. A no-op in a browser.
 */
export function useReportAccount(name: string | null | undefined): void {
  useEffect(() => {
    if (!name) return
    window.niobeDesktop?.reportAccount?.(name)
  }, [name])
}

/** Which side(s) of the desktop app have a downloaded update ready to install/apply. */
export type UpdateChannel = 'app' | 'spa' | 'both' | null

/** Pure classifier over the raw update state — kept separate from the hook so it's trivial to test. */
export function describeUpdate(state: UpdateState | null): UpdateChannel {
  if (!state) return null
  const app = state.app === 'ready'
  const spa = state.spa === 'ready'
  if (app && spa) return 'both'
  if (app) return 'app'
  if (spa) return 'spa'
  return null
}

/**
 * Reactive view of the desktop app's update state, for the in-app "update ready — Restart" banner.
 *
 * A no-op in a browser (`window.niobeDesktop` is absent, so `channel` stays `null` and `restart`
 * does nothing). Push-based like `useAgentBusy`, but sourced from IPC rather than a module-local
 * store: the desktop app owns this state, this hook only mirrors it.
 */
export function useUpdateReady(): {
  channel: UpdateChannel
  state: UpdateState | null
  restarting: boolean
  restart: () => void
} {
  const [state, setState] = useState<UpdateState | null>(null)
  const [restarting, setRestarting] = useState(false)

  useEffect(() => {
    const updates = window.niobeDesktop?.updates
    if (!updates) return
    // An IPC failure here (an older shell, a renderer shutting down) is not the member's problem:
    // the banner simply stays hidden until the next onChange.
    updates.get().then(setState).catch(() => undefined)
    return updates.onChange(setState)
  }, [])

  const channel = describeUpdate(state)
  const restart = (): void => {
    setRestarting(true)
    window.niobeDesktop?.updates?.restart()
  }

  return { channel, state, restarting, restart }
}
